import { useState, useEffect, useCallback } from 'react';
import { fetchApi } from '../../lib/api';

export default function InventorDetail({ inventorId, onUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchApi(`/api/admin/inventors/${inventorId}/detail`);
      setData(result);
      setForm(result.profile || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [inventorId]);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await fetchApi(`/api/admin/inventors/${inventorId}/profile`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      setEditing(false);
      await load();
      onUpdate && onUpdate();
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-400">Loading...</div>;
  if (!data) return <div className="text-sm text-gray-400">Inventor not found</div>;

  const { user, profile, inviting_company, connections, nda_records } = data;
  const inputCls = "w-full px-3 py-2 rounded-input border-2 border-vault-border text-sm text-vault-navy bg-white focus:border-vault-gold focus:outline-none";
  const labelCls = "block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1";

  return (
    <div className="space-y-8">
      {/* Profile */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-vault-navy">Inventor Profile</h3>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="text-vault-gold text-sm font-semibold">Edit</button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="text-gray-400 text-sm">Cancel</button>
              <button onClick={saveProfile} disabled={saving} className="text-vault-gold text-sm font-semibold">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <div><label className={labelCls}>Full Name</label><input className={inputCls} value={form.full_name || ''} onChange={e => setForm({...form, full_name: e.target.value})} /></div>
            <div><label className={labelCls}>Bio</label><textarea className={inputCls} value={form.bio || ''} onChange={e => setForm({...form, bio: e.target.value})} rows={3} /></div>
            <div><label className={labelCls}>Portfolio URL</label><input className={inputCls} value={form.portfolio_url || ''} onChange={e => setForm({...form, portfolio_url: e.target.value})} /></div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {profile?.avatar_url && <img src={profile.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover mb-2" />}
            <div><span className="text-gray-400 w-24 inline-block">Name:</span> <span className="text-vault-navy font-medium">{profile?.full_name || user?.display_name}</span></div>
            <div><span className="text-gray-400 w-24 inline-block">Email:</span> <span className="text-vault-navy">{user?.email}</span></div>
            <div><span className="text-gray-400 w-24 inline-block">Bio:</span> <span className="text-vault-navy">{profile?.bio || '—'}</span></div>
            <div><span className="text-gray-400 w-24 inline-block">Portfolio:</span> {profile?.portfolio_url ? <a href={profile.portfolio_url} target="_blank" rel="noreferrer" className="text-vault-gold hover:underline">{profile.portfolio_url}</a> : '—'}</div>
            <div><span className="text-gray-400 w-24 inline-block">Invited by:</span> <span className="text-vault-navy font-medium">{inviting_company?.company_name || '—'}</span></div>
            <div><span className="text-gray-400 w-24 inline-block">Joined:</span> <span className="text-vault-navy">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</span></div>
          </div>
        )}
      </div>

      {/* Connections */}
      <div>
        <h3 className="font-semibold text-vault-navy mb-3">Company Connections ({connections.length})</h3>
        {connections.length === 0 ? <p className="text-sm text-gray-400">None</p> : (
          <div className="space-y-2">
            {connections.map(c => (
              <div key={c.id} className="border border-vault-border rounded-btn p-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  {c.company?.logo_url && <img src={c.company.logo_url} alt="" className="w-6 h-6 rounded" />}
                  <span className="font-medium text-vault-navy">{c.company?.company_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${c.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : c.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}>{c.status}</span>
                  {c.nda_agreed && <span className="text-[10px] text-emerald-600">NDA v{c.nda_version_agreed}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* NDA Records */}
      <div>
        <h3 className="font-semibold text-vault-navy mb-3">NDA Records ({nda_records.length})</h3>
        {nda_records.length === 0 ? <p className="text-sm text-gray-400">None</p> : (
          <div className="space-y-2">
            {nda_records.map(nda => (
              <div key={nda.id} className="border border-vault-border rounded-btn p-3 text-sm space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-vault-navy">NDA v{nda.nda_version}</span>
                  {nda.document_url && <a href={nda.document_url} target="_blank" rel="noreferrer" className="text-vault-gold text-xs font-semibold hover:underline">View Document</a>}
                </div>
                {nda.inventor_signed_at && (
                  <div className="text-xs text-gray-500">
                    Signed: <span className="text-vault-navy">{nda.inventor_signature_text}</span> — {new Date(nda.inventor_signed_at).toLocaleString()} — IP: {nda.inventor_ip_address}
                  </div>
                )}
                {nda.company_action_at && (
                  <div className="text-xs text-gray-500">
                    Company {nda.company_action_type}: <span className="text-vault-navy">{nda.company_signature_text}</span> — {new Date(nda.company_action_at).toLocaleString()} — IP: {nda.company_ip_address}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
